// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Metadata data pipeline — pure functions, no DOM.

import { Angles, TreeTypes } from "./constants.js";

// ---------------------------------------------------------------------------
// Tree-type predicates
// ---------------------------------------------------------------------------

/**
 * True for tree types that lay out leaves along a linear axis (Rectangular,
 * Hierarchical, Diagonal); false for radially-arranged types (Circular, Radial).
 *
 * @param {string} type - A TreeTypes value.
 * @returns {boolean}
 */
export function isOrthogonal(type) {
  switch (type) {
    case TreeTypes.Rectangular:
    case TreeTypes.Hierarchical:
    case TreeTypes.Diagonal:
      return true;

    case TreeTypes.Circular:
    case TreeTypes.Radial:
      return false;

    default:
      throw new Error(`isOrthogonal: unrecognised tree type "${type}"`);
  }
}

// ---------------------------------------------------------------------------
// Leaf-label predicates
// ---------------------------------------------------------------------------

/**
 * True when leaf labels (and metadata blocks) should be aligned to a common
 * outer edge rather than drawn at each leaf tip.  Circular, Rectangular and
 * Hierarchical trees respect props.alignLabels; Diagonal and Radial are always
 * unaligned.
 *
 * @param {object} props
 * @returns {boolean}
 */
export function getAlignLeafLabels(props) {
  return (
    !!props.alignLabels &&
    props.type !== TreeTypes.Diagonal &&
    props.type !== TreeTypes.Radial
  );
}

/**
 * True when leaf labels should be rendered: both the global showLabels switch
 * and the leaf-specific showLeafLabels switch must be on.
 *
 * @param {object} props
 * @returns {boolean}
 */
export function hasLeafLabels(props) {
  return !!(props.showLabels && props.showLeafLabels);
}

// ---------------------------------------------------------------------------
// Metadata presence checks
// ---------------------------------------------------------------------------

/**
 * True when props.metadata is a non-empty object and props.blocks is a
 * non-empty array.
 *
 * @param {object} props
 * @returns {boolean}
 */
export function hasMetadata(props) {
  return (
    !!props.metadata &&
    Array.isArray(props.blocks) &&
    props.blocks.length > 0
  );
}

/**
 * True when both showLabels and showBlockLabels are truthy.
 *
 * @param {object} props
 * @returns {boolean}
 */
export function hasMetadataLabels(props) {
  return !!(props.showLabels && props.showBlockLabels);
}

/**
 * True when metadata column headers should be rendered above the blocks:
 *   - hasMetadata() must be true
 *   - showLabels && showBlockHeaders must both be truthy
 *   - type is Diagonal, OR (alignLabels is true AND type is Rectangular or Hierarchical)
 *
 * @param {object} graph
 * @param {object} props
 * @returns {boolean}
 */
export function hasMetadataHeaders(graph, props) {
  if (!hasMetadata(props)) return false;
  if (!props.showLabels || !props.showBlockHeaders) return false;

  const type = props.type;
  return (
    type === TreeTypes.Diagonal ||
    (
      props.alignLabels &&
      (type === TreeTypes.Rectangular || type === TreeTypes.Hierarchical)
    )
  );
}

// ---------------------------------------------------------------------------
// Sizing helpers
// ---------------------------------------------------------------------------

/**
 * Width of a single metadata column in pixels: blockSize + blockPadding.
 *
 * @param {object} props
 * @returns {number}
 */
export function getMetadataColumnWidth(props) {
  const blockSize = props.blockSize ?? 16;
  const blockPadding = props.blockPadding ?? 4;
  return blockSize + blockPadding;
}

/**
 * Font size for metadata column header labels.
 * Capped at blockSize so headers always fit inside their column.
 *
 * @param {object} props
 * @returns {number}
 */
export function getMetadataHeaderFontSize(props) {
  const blockSize = props.blockSize ?? 16;
  const blockHeaderFontSize = props.blockHeaderFontSize ?? 13;
  return Math.min(blockSize, blockHeaderFontSize);
}

// ---------------------------------------------------------------------------
// Pixel offset (gap between leaf-end and first metadata column)
// ---------------------------------------------------------------------------

// Module-level canvas context, created lazily.  Guarded against SSR / worker
// environments where `document` is unavailable.
let _canvasCtx = null;

function getCanvasCtx() {
  if (_canvasCtx) return _canvasCtx;
  if (typeof document !== "undefined") {
    _canvasCtx = document.createElement("canvas").getContext("2d");
  }
  return _canvasCtx;
}

function measureTextWidth(text, font) {
  const ctx = getCanvasCtx();
  if (ctx) {
    ctx.font = font;
    return ctx.measureText(text).width;
  }
  // Fallback estimate: average character width ~ 0.6 × fontSize.
  const fontSize = parseFloat(font) || 16;
  return text.length * fontSize * 0.6;
}

/**
 * Compute the pixel gap reserved before the first metadata column begins.
 * The returned object has at minimum a `.length` field — the horizontal
 * clearance that blocks-data and headers-data must add to their offsetX
 * values so blocks don't overlap node shapes or leaf labels.
 *
 * Derivation:
 *   length = visibleNodeSize + maxLabelWidth
 *
 * where visibleNodeSize = nodeSize (full diameter, not radius), and
 * maxLabelWidth is measured via canvas when showLabels+showLeafLabels,
 * otherwise 0.  A 4 px gap is added after the label text.
 *
 * @param {object} graph
 * @param {object} props
 * @returns {{ length: number }}
 */
export function getPixelOffsets(graph, props) {
  const nodeSize = props.showShapes !== false ? (props.nodeSize ?? 14) : 0;
  const fontSize = props.fontSize ?? 16;
  const fontFamily = (props.fontFamily ?? "monospace").replace(/"/g, "'");
  const font = `${fontSize}px ${fontFamily}`;

  // Local divergence from the upstream pin (7a8a07d): widen the gutter between
  // the tip label and its first metadata column from a flat 4 px to a
  // font-relative ~0.75em, so columns sit clear of the labels rather than
  // butting against them. See VENDOR.md "Local modifications".
  const labelGutter = Math.round(fontSize * 0.75); // ~12 px at the default 16 px font
  let maxLabelWidth = 0;
  if (hasLeafLabels(props)) {
    let maxLabel = "";
    for (const node of graph.leaves) {
      if (node.isHidden) continue;
      const label = node.label ?? node.id ?? "";
      if (label.length > maxLabel.length) {
        maxLabel = label;
      }
    }
    if (maxLabel.length > 0) {
      maxLabelWidth = measureTextWidth(maxLabel, font) + labelGutter;
    }
  }

  return {
    length: nodeSize + maxLabelWidth,
  };
}

// ---------------------------------------------------------------------------
// Blocks data
// ---------------------------------------------------------------------------

/**
 * Compute the render datum for every (leaf, column) pair that has a colour.
 *
 * Guards on `!node.isHidden` and a truthy metadata entry (node.shape is always
 * set by applyStyles() for leaves).
 *
 * alignEffective follows getAlignLeafLabels(): Circular, Rectangular, and
 * Hierarchical trees respect props.alignLabels; Diagonal and Radial always
 * return false regardless of the prop.
 *
 * @param {object} graph
 * @param {object} props
 * @returns {Array<{ node, position: [number,number], offsetX: number, colour: number[], columnName: string }>}
 */
export function getBlocksData(graph, props) {
  if (!hasMetadata(props)) return [];

  const columnWidth = getMetadataColumnWidth(props);
  const branchScale = graph.branchScale ?? 1;
  const totalSubtreeLength = graph.root.totalSubtreeLength;
  const metadataValues = props.metadata;
  const metadataColumns = props.blocks;

  const alignEffective = getAlignLeafLabels(props);

  const data = [];

  for (const node of graph.leaves) {
    if (node.isHidden) continue;
    if (!(node.id in metadataValues)) continue;

    const nodeMetadata = metadataValues[node.id];

    const alignOffset = alignEffective
      ? branchScale * (totalSubtreeLength - node.distanceFromRoot)
      : 0;

    const position = [
      node.x + alignOffset * Math.cos(node.angle),
      node.y + alignOffset * Math.sin(node.angle),
    ];

    let xOffset = 0;
    for (let c = 0; c < metadataColumns.length; c++) {
      const columnName = metadataColumns[c];
      const entry = nodeMetadata[columnName];

      if (entry && entry.colour != null) {
        data.push({
          node,
          position,
          offsetX: xOffset + columnWidth / 2,
          colour: entry.colour,
          columnName,
        });
      }

      xOffset += columnWidth;
    }
  }

  return data;
}

// ---------------------------------------------------------------------------
// Headers data
// ---------------------------------------------------------------------------

/**
 * Compute one datum per metadata column to be rendered as a rotated header label.
 *
 * The anchor leaf is the first leaf in preorder (graph.leaves[0]).
 *
 * Key datum fields:
 *   - `angle` is stored as `360 - (rawAngle / Degrees360) * 360` (degrees, not
 *     radians).  The render layer adds 180 to get the final rotation, so
 *     `degrees = datum.angle + 180`.
 *   - `offsetY` is `(rawAngle === 0 ? 1 : -1) * blockSize`.  When
 *     hasMetadataLabels is false, rawAngle is adjusted by -90° before this
 *     calculation (i.e. `firstLeaf.angle - Degrees90`).
 *   - `inverted` is true when the anchor leaf's angle is between 90° and 270°.
 *
 * @param {object} graph
 * @param {object} props
 * @returns {Array<{ node, inverted: boolean, position: [number,number], offsetX: number, offsetY: number, text: string, angle: number }>}
 */
export function getHeadersData(graph, props) {
  if (!hasMetadataHeaders(graph, props)) return [];

  const firstLeaf = graph.leaves[0];
  if (!firstLeaf) return [];

  const columnWidth = getMetadataColumnWidth(props);
  const branchScale = graph.branchScale ?? 1;
  const totalSubtreeLength = graph.root.totalSubtreeLength;
  const blockSize = props.blockSize ?? 16;
  const metadataColumns = props.blocks;

  const alignEffective = getAlignLeafLabels(props);

  const inverted =
    firstLeaf.angle > Angles.Degrees90 && firstLeaf.angle < Angles.Degrees270;

  const alignOffset = alignEffective
    ? branchScale * (totalSubtreeLength - firstLeaf.distanceFromRoot)
    : 0;

  const position = [
    firstLeaf.x + alignOffset * Math.cos(firstLeaf.angle),
    firstLeaf.y + alignOffset * Math.sin(firstLeaf.angle),
  ];

  // When hasMetadataLabels is false, rotate the header angle by -90° so
  // labels run perpendicular to the leaf direction.
  let rawAngle = firstLeaf.angle;
  if (!hasMetadataLabels(props)) {
    rawAngle -= Angles.Degrees90;
  }

  // offsetY pushes the header away from the block row;
  // sign uses the `(angle === 0 ? 1 : -1)` check.
  const offsetY = (rawAngle === 0 ? 1 : -1) * blockSize;

  // datum.angle is stored in degrees (not radians):
  // 360 - (rawAngle / Degrees360) * 360.  The render layer then does +180.
  const datumAngle = 360 - (rawAngle / Angles.Degrees360) * 360;

  const data = [];
  let xOffset = 0;

  for (let index = 0; index < metadataColumns.length; index++) {
    const columnName = metadataColumns[index];

    data.push({
      node: firstLeaf,
      inverted,
      position,
      offsetX: xOffset + columnWidth / 2,
      offsetY,
      text: columnName,
      angle: datumAngle,
    });

    xOffset += columnWidth;
  }

  return data;
}

// ---------------------------------------------------------------------------
// Pixel-offset accessor (projects a datum's offsetX/offsetY into canvas space)
// ---------------------------------------------------------------------------

/**
 * Build a function that converts a datum's (offsetX, offsetY) into pixel
 * deltas (dx, dy) relative to datum.position.
 *
 * blockOffset = getPixelOffsets(graph, props).length is added to datum.offsetX
 * before projection so blocks clear leaf labels and node shapes.
 *
 * @param {object} graph
 * @param {object} props
 * @returns {function(datum): [number, number]}
 */
export function makePixelOffsetAccessor(graph, props) {
  const blockOffset = getPixelOffsets(graph, props).length;

  return function pixelOffset(datum) {
    const adj = datum.offsetX + blockOffset;
    if (datum.offsetY != null) {
      return [
        adj * Math.cos(datum.node.angle) + datum.offsetY * Math.sin(datum.node.angle),
        adj * Math.sin(datum.node.angle) + datum.offsetY * Math.cos(datum.node.angle),
      ];
    }
    return [
      adj * Math.cos(datum.node.angle),
      adj * Math.sin(datum.node.angle),
    ];
  };
}
